import { useState } from "react";

import { phase } from "../domain/plan";
import { PAIN_LIMIT, TALK_WORDS, askHowWas, painRule } from "../domain/readiness";
import type { HowWas, JointKey, SessionKey } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import { recordHowWas } from "../store/toggles";
import { ActionBar } from "./ActionBar";
import { Chip } from "./Chip";
import { Segmented } from "./Segmented";
import { Sheet } from "./Sheet";
import "./HowWasIt.css";

/**
 * "How was it?" (board HowWasIt; ADR-054): one tap from three, then "Anything hurt?" as joint
 * chips, and the 0–10 row only once a joint is picked. The finished players and P5's Simple
 * player put it over their finished screen; `HowWasAsk` is the same sheet with the store wired.
 */

export type HowWasAnswer = Pick<HowWas, "fit" | "pain" | "talk">;

export interface HowWasItProps {
  /** "Strength & balance · 25 min · one tap" */
  sub?: string;
  onSave: (answer: HowWasAnswer) => void;
  /** writes nothing */
  onSkip: () => void;
  /** the sheet's words in the player's language (the Simple player in Hindi, ADR-055) */
  words?: (en: string) => string;
  /** ask "Could you talk?" — after a walk, a treadmill run or a walk-day session (ADR-065) */
  talk?: boolean;
}

const FITS: { value: HowWas["fit"]; label: string }[] = [
  { value: "easy", label: "Too easy" },
  { value: "right", label: "About right" },
  { value: "hard", label: "Too hard" },
];

const JOINTS: { value: JointKey; label: string }[] = [
  { value: "neck", label: "Neck" },
  { value: "shoulder", label: "Shoulder" },
  { value: "back", label: "Back" },
  { value: "hip", label: "Hip" },
  { value: "knee", label: "Knee" },
  { value: "ankle", label: "Ankle or foot" },
  { value: "wrist", label: "Wrist" },
];

const TALKS = (["easy", "talk", "hard"] as const).map((value) => ({ value, label: TALK_WORDS[value] }));

const SCORES = Array.from({ length: 11 }, (_, i) => i);

const same = (en: string) => en;

export function HowWasIt({ sub, onSave, onSkip, words: w = same, talk: askTalk = false }: HowWasItProps) {
  const [fit, setFit] = useState<HowWas["fit"] | null>(null);
  const [talk, setTalk] = useState<NonNullable<HowWas["talk"]> | null>(null);
  const [joint, setJoint] = useState<JointKey | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const needScore = joint !== null && score === null;

  return (
    <Sheet
      large
      title={w("How was it?")}
      sub={sub}
      onClose={onSkip}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: w("Skip"), onClick: onSkip }}
          primary={{
            label: w("Save"),
            // no `reason`: the bar would swap Skip for it, and Skip must stay one tap away
            disabled: fit === null || needScore,
            onClick: () => {
              if (fit === null) return;
              // the talk row is optional: the fit row stays the one required answer
              onSave({ fit, ...(joint !== null && score !== null ? { pain: { joint, score } } : {}), ...(talk ? { talk } : {}) });
            },
          }}
        />
      }
    >
      <div className="k-howwas">
        {askTalk ? (
          <div className="k-howwas__talk">
            <h3 className="k-howwas__h" id="hw-talk">
              {w("Could you talk?")}
            </h3>
            <Segmented options={TALKS.map((t) => ({ ...t, label: w(t.label) }))} value={talk} onChange={setTalk} ariaLabel={w("Could you talk?")} />
          </div>
        ) : null}
        <Segmented options={FITS.map((f) => ({ ...f, label: w(f.label) }))} value={fit} onChange={setFit} ariaLabel={w("How was it?")} />
        <div className="k-howwas__hurt">
          <h3 className="k-howwas__h" id="hw-hurt">
            {w("Anything hurt?")}
          </h3>
          <div className="k-howwas__chips" role="group" aria-labelledby="hw-hurt">
            {JOINTS.map((j) => (
              <Chip
                key={j.value}
                size="lg"
                selected={joint === j.value}
                onClick={() => {
                  setJoint(joint === j.value ? null : j.value);
                  setScore(null);
                }}
              >
                {w(j.label)}
              </Chip>
            ))}
          </div>
          {joint !== null ? (
            <div className="k-howwas__scale">
              <p className="k-howwas__q" id="hw-much">
                {w("How much? 0 is none, 10 the worst you can imagine.")}
              </p>
              <div className="seg k-seg k-howwas__seg" role="group" aria-labelledby="hw-much">
                {SCORES.map((n) => (
                  <button key={n} type="button" className={score === n ? "on" : ""} aria-pressed={score === n} onClick={() => setScore(n)}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="k-howwas__fine">{w("Pick a joint and Kaya asks how much, 0 to 10. Nothing picked means nothing hurt.")}</p>
          )}
        </div>
      </div>
    </Sheet>
  );
}

/** Skipped this run of the app: Skip writes nothing, and the sheet is not asked again for that day. */
const skipped = new Set<string>();

/**
 * The sheet with the store wired: asked once a day (`readiness.ts::askHowWas`), saved through
 * `toggles.ts::recordHowWas`, which also moves the template's dose (Steady) or pins the phase
 * (Kaya, too hard twice) — the toast says what changed. Render it over a finished screen.
 */
export function HowWasAsk({
  ds,
  sessionKey,
  short,
  sub,
  words,
  talk,
}: {
  ds: string;
  sessionKey?: SessionKey;
  short?: boolean;
  sub?: string;
  words?: (en: string) => string;
  /** ask the talk test too (`readiness.ts::askTalk`, ADR-065) */
  talk?: boolean;
}) {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const [open, setOpen] = useState(() => askHowWas(S, ds) && !skipped.has(ds));
  if (!open) return null;

  const save = (a: HowWasAnswer) => {
    let said = "Thanks — noted for next time";
    update((d) => {
      const r = recordHowWas(d, ds, { ...a, ...(sessionKey ? { key: sessionKey } : {}), ...(short ? { short } : {}) }, phase(d));
      const hurt = a.pain && a.pain.score > PAIN_LIMIT ? painRule(d, ds)[a.pain.joint] : undefined;
      if (hurt) said = hurt.line;
      else if (r.pin) said = `Too hard twice: phase ${r.pin.to + 1} for now`;
      else if (r.dose && (r.dose.change !== "same" || r.dose.variant || r.dose.offer)) said = r.dose.why;
    });
    setOpen(false);
    showToast(said);
  };

  return (
    <HowWasIt
      sub={sub}
      words={words}
      talk={talk}
      onSave={save}
      onSkip={() => {
        skipped.add(ds);
        setOpen(false);
      }}
    />
  );
}
